// ./application-steps/StepContent7.js

import React, { memo } from "react";
import Grid from "@mui/material/Grid";
import FormHelperText from "@mui/material/FormHelperText";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormCheckbox from "../../helpers/FormCheckbox";

function StepContent7({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
}) {
  const handleCheckboxChange = (index) => (event) => {
    const updatedAffiliations = [...formData.affiliations];
    updatedAffiliations[index].selected = event.target.checked;

    if (!event.target.checked) {
      updatedAffiliations[index].moreInfo = "";
    }
    setFormData((prev) => ({
      ...prev,
      affiliations: updatedAffiliations,
    }));
  };

  const handleMoreInfoChange = (index) => (event) => {
    const updatedAffiliations = [...formData.affiliations];
    updatedAffiliations[index].moreInfo = event.target.value;

    setFormData((prev) => ({
      ...prev,
      affiliations: updatedAffiliations,
    }));
  };

  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) return "error";
    if (isRequired && formData[field]) return "success";
    if (!isRequired && !formData[field]) return "normal";
    return "";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="affiliations-form"
    >
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} textAlign="center">
          Affiliations
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mx: "auto" }}
          textAlign="left"
        >
          Some awards are only open to students who have certain affiliations or
          connections. This section helps us identify which awards you might be
          eligible for.
        </Typography>
      </Stack>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Please read carefully</strong> and select all affiliations
          that apply to you. Some selections will ask for{" "}
          <strong>additional details</strong>.
        </Typography>
      </Alert>

      <Divider sx={{ my: 2 }} />

      <form className="form-group">
        <FormRow spacing={2}>
          <Grid item xs={12}>
            <Stack spacing={1.5}>
              {formData.affiliations?.map((affiliation, index) => {
                const hasMoreInfo = !!affiliation?.moreInfoText;
                const moreInfoError =
                  stepErrors[`affiliations[${index}].moreInfo`];

                return (
                  <Paper
                    key={affiliation?.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: affiliation?.selected
                        ? "background.default"
                        : "background.paper",
                    }}
                  >
                    <FormCheckbox
                      label={affiliation?.name}
                      checked={affiliation?.selected}
                      onChange={handleCheckboxChange(index)}
                      name={`affiliation-${affiliation?.id}`}
                      isReadOnly={isReadOnly}
                      status={getStatus(
                        `affiliations[${index}].selected`,
                        false
                      )}
                      helperText={
                        stepErrors[`affiliations[${index}].selected`] || ""
                      }
                    />

                    {affiliation.selected && hasMoreInfo && (
                      <Box sx={{ mt: 1, pl: { xs: 0, sm: 4 } }}>
                        <FormInput
                          label={`More info`}
                          name={`affiliations[${index}].moreInfo`}
                          value={affiliation?.moreInfo}
                          onChange={handleMoreInfoChange(index)}
                          multiline
                          rows={3}
                          variant="outlined"
                          fullWidth
                          disabled={isReadOnly}
                          required
                          error={Boolean(moreInfoError)}
                          helperText={moreInfoError || ""}
                          status={getStatus(
                            `affiliations[${index}].moreInfo`,
                            true
                          )}
                        />

                        {/* clearer hint when it's required and empty */}
                        {hasMoreInfo && !affiliation.moreInfo && (
                          <FormHelperText error sx={{ mt: 0.5 }}>
                            {`Required: ${affiliation?.moreInfoText}`}
                          </FormHelperText>
                        )}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </FormRow>
      </form>
    </Paper>
  );
}

export default memo(StepContent7);
